import React, { useState } from 'react';
import '../styles/components/contact.css';
import { githubIcon, linkedinIcon, downloadIcon } from '../assets';
import { useLanguage } from '../contexts/LanguageContext';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
/** Public access key (Web3Forms). Override with REACT_APP_WEB3FORMS_ACCESS_KEY if needed. */
const WEB3FORMS_ACCESS_KEY_DEFAULT = '7d8fa27e-df16-43e6-88ee-06dd0c07c9c4';

const Contact: React.FC = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSending, setIsSending] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setFeedbackMessage(t.contact.required);
      return;
    }

    const accessKey =
      process.env.REACT_APP_WEB3FORMS_ACCESS_KEY?.trim() || WEB3FORMS_ACCESS_KEY_DEFAULT;
    if (!accessKey) {
      setFeedbackMessage(t.contact.formNotConfigured);
      return;
    }

    setIsSending(true);
    setFeedbackMessage('');

    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: accessKey,
          subject: t.contact.emailSubject,
          from_name: formData.name,
          name: formData.name,
          email: formData.email,
          message: formData.message,
        }),
      });
      const data = (await res.json()) as { success?: boolean; message?: string };

      if (data.success) {
        setFeedbackMessage(t.contact.success);
        setFormData({ name: '', email: '', message: '' });
      } else {
        console.error('Web3Forms:', data.message ?? res.status);
        setFeedbackMessage(t.contact.error);
      }
    } catch {
      setFeedbackMessage(t.contact.error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="contact-section" id="contact">
      <div className='header-container'>
        <div className='title-container'>
          <h1 className="section-title">{t.contact.title}</h1>
          <p className="contact-description">
            {t.contact.description}
          </p>
        </div>
        <div className='social-container'>
          <a href="https://github.com/atilarocha" target="_blank" rel="noopener noreferrer">
            <img src={githubIcon} alt="GitHub" className="social-icon" />
          </a>
          <a href="https://linkedin.com/in/atila-rocha" target="_blank" rel="noopener noreferrer">
            <img src={linkedinIcon} alt="LinkedIn" className="social-icon" />
          </a>
          <a href="/docs/Currículo Átila Rocha.pdf" className="download-button">
            <div className="button-content">
              <img src={downloadIcon} alt="Download" className="download-icon" />
              <span className="button-text">{t.contact.downloadCv}</span>
            </div>
          </a>
        </div>
      </div>
      <div className="contact-cta">
        <div>
          <span className="contact-cta-label">{t.contact.ctaLabel}</span>
          <p>{t.contact.ctaDescription}</p>
        </div>
        <a className="email-button" href={`mailto:atila.o.rocha@gmail.com?subject=${encodeURIComponent(t.contact.emailSubject)}`}>
          atila.o.rocha@gmail.com
        </a>
      </div>
      <form className="contact-form" onSubmit={handleSubmit}>
        <label htmlFor="name">{t.contact.nameLabel}</label>
        <input type="text" id="name" placeholder={t.contact.namePlaceholder} value={formData.name} onChange={handleChange} />

        <label htmlFor="email">{t.contact.emailLabel}</label>
        <input type="email" id="email" placeholder={t.contact.emailPlaceholder} value={formData.email} onChange={handleChange} />

        <label htmlFor="message">{t.contact.messageLabel}</label>
        <textarea id="message" placeholder={t.contact.messagePlaceholder} value={formData.message} onChange={handleChange}></textarea>

        <button type="submit" disabled={isSending}>{t.contact.submit}</button>
        {isSending && <p>{t.contact.sending}</p>}
        {feedbackMessage && <p>{feedbackMessage}</p>}
      </form>
    </section>
  );
};

export default Contact;
